import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useApp } from '@/context/AppContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api, { errMsg } from '@/lib/api';
import { toast } from 'sonner';
import { money, fmtDateShort } from '@/lib/helpers';
import {
  Search, PackageSearch, AlertTriangle, RefreshCw, CheckCircle2, Clock, X,
  ChevronRight, ChevronLeft, Link2, PencilLine, ShieldCheck, Loader2, Receipt,
} from 'lucide-react';

const REASON_BADGE = {
  CODE_NOT_FOUND: { label: 'Unknown Code', cls: 'bg-sky-500/15 text-sky-600 border-sky-500/30' },
  ORIGINAL_CODE_ALREADY_SOLD: { label: 'Sold-Code Replacement', cls: 'bg-amber-500/15 text-amber-600 border-amber-500/30' },
  ORIGINAL_CODE_IN_TRANSIT: { label: 'In-Transit Replacement', cls: 'bg-indigo-500/15 text-indigo-600 border-indigo-500/30' },
  NO_TAG: { label: 'Untagged (No Tag)', cls: 'bg-violet-500/15 text-violet-600 border-violet-500/30' },
  DUPLICATE_IN_SALE: { label: 'Duplicate In Message', cls: 'bg-fuchsia-500/15 text-fuchsia-600 border-fuchsia-500/30' },
  AUTO_PLACEHOLDER: { label: 'Auto-created', cls: 'bg-blue-500/15 text-blue-600 border-blue-500/30' },
  MALFORMED_CODE: { label: 'Malformed Code', cls: 'bg-blue-500/15 text-blue-600 border-blue-500/30' },
  ITEM_DETAILS_UNRESOLVED: { label: 'Item Unresolved', cls: 'bg-blue-500/15 text-blue-600 border-blue-500/30' },
};
const SOURCE_LABEL = {
  whatsapp_paste: 'WhatsApp Paste',
  screenshot_import: 'WhatsApp Screenshot',
};
const AGE_BADGE = {
  normal: 'bg-muted text-muted-foreground border-border',
  attention: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  overdue: 'bg-rose-500/15 text-rose-600 border-rose-500/30',
};

const reasonBadge = (r) => REASON_BADGE[r] || { label: r || '—', cls: 'bg-muted text-muted-foreground border-border' };
const sourceLabel = (s) => SOURCE_LABEL[s] || s || '—';

export default function InventoryReview() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { stores, currentStore } = useApp();
  const storeName = (id) => (stores || []).find((s) => s.id === id)?.name || id;

  const [q, setQ] = useState('');
  const [storeScope, setStoreScope] = useState('ALL');
  const [reason, setReason] = useState('ALL');
  const [source, setSource] = useState('ALL');
  const [status, setStatus] = useState('unresolved');
  const [salesperson, setSalesperson] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const [selected, setSelected] = useState(() => new Set());
  const [resolveRow, setResolveRow] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const params = useMemo(() => {
    const p = { page, page_size: pageSize, status };
    if (q.trim()) p.q = q.trim();
    if (storeScope !== 'ALL') p.store_id = storeScope;
    if (reason !== 'ALL') p.reason = reason;
    if (source !== 'ALL') p.source = source;
    if (salesperson.trim()) p.salesperson = salesperson.trim();
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [page, status, q, storeScope, reason, source, salesperson, dateFrom, dateTo]);

  const { data: summary } = useQuery({
    queryKey: ['inv-review-summary', storeScope],
    queryFn: async () => (await api.get('/inventory-review/summary', {
      params: storeScope === 'ALL' ? {} : { store_id: storeScope },
    })).data.data,
  });

  const { data: list, isLoading, isFetching } = useQuery({
    queryKey: ['inv-review', params],
    queryFn: async () => (await api.get('/inventory-review', { params })).data.data,
    keepPreviousData: true,
  });

  const rows = list?.items || [];
  const total = list?.total || 0;
  const pages = list?.pages || 1;

  const refresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['inv-review'] });
    qc.invalidateQueries({ queryKey: ['inv-review-summary'] });
  }, [qc]);

  const resetToFirst = (fn) => (v) => { setPage(1); fn(v); };

  // ---- selection (unresolved only can be reviewed) ----
  const selectableIds = useMemo(() => rows.filter((r) => r.needs_review).map((r) => r.garment_id), [rows]);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => (selectableIds.every((id) => s.has(id)) ? new Set() : new Set(selectableIds)));
  const clearSel = () => setSelected(new Set());

  const applyCard = (patch) => {
    setPage(1);
    setStatus(patch.status ?? 'unresolved');
    setReason(patch.reason ?? 'ALL');
  };

  return (
    <Page>
      <TopBar title="Inventory Review" right={
        <Button size="sm" variant="outline" className="h-9 rounded-full" data-testid="inv-review-refresh" onClick={refresh}>
          <RefreshCw className={`h-4 w-4 lg:mr-1.5 ${isFetching ? 'animate-spin' : ''}`} /><span className="hidden lg:inline">Refresh</span>
        </Button>
      } />

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 px-4 pt-4 md:grid-cols-3 lg:grid-cols-5 lg:px-6">
        <SummaryCard testid="inv-card-needs" icon={PackageSearch} tone="primary"
          label="Needs Review" value={summary?.needs_review ?? '–'}
          active={status === 'unresolved' && reason === 'ALL'} onClick={() => applyCard({ status: 'unresolved', reason: 'ALL' })} />
        <SummaryCard testid="inv-card-unknown" icon={AlertTriangle} tone="sky"
          label="Unknown Codes" value={summary?.unknown_codes ?? '–'}
          active={status === 'unresolved' && reason === 'CODE_NOT_FOUND'} onClick={() => applyCard({ status: 'unresolved', reason: 'CODE_NOT_FOUND' })} />
        <SummaryCard testid="inv-card-sold" icon={Receipt} tone="amber"
          label="Sold-Code Replacements" value={summary?.sold_replacements ?? '–'}
          active={status === 'unresolved' && reason === 'ORIGINAL_CODE_ALREADY_SOLD'} onClick={() => applyCard({ status: 'unresolved', reason: 'ORIGINAL_CODE_ALREADY_SOLD' })} />
        <SummaryCard testid="inv-card-resolved" icon={CheckCircle2} tone="emerald"
          label="Resolved Today" value={summary?.resolved_today ?? '–'}
          active={status === 'resolved'} onClick={() => applyCard({ status: 'resolved', reason: 'ALL' })} />
        <SummaryCard testid="inv-card-older" icon={Clock} tone="rose"
          label="Older Than 7 Days" value={summary?.older_than_7 ?? '–'}
          active={false} onClick={() => applyCard({ status: 'unresolved', reason: 'ALL' })} />
      </div>

      {/* Filters */}
      <div className="mt-4 flex flex-wrap items-center gap-2 px-4 lg:px-6">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => resetToFirst(setQ)(e.target.value)} placeholder="Code or invoice" className="h-11 rounded-xl pl-9" data-testid="inv-review-search" />
        </div>
        {stores.length > 1 && (
          <Select value={storeScope} onValueChange={resetToFirst(setStoreScope)}>
            <SelectTrigger className="h-11 w-[150px] rounded-xl" data-testid="inv-review-store"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Stores</SelectItem>
              {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={reason} onValueChange={resetToFirst(setReason)}>
          <SelectTrigger className="h-11 w-[170px] rounded-xl" data-testid="inv-review-reason"><SelectValue placeholder="Reason" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Reasons</SelectItem>
            <SelectItem value="CODE_NOT_FOUND">Unknown Code</SelectItem>
            <SelectItem value="ORIGINAL_CODE_ALREADY_SOLD">Sold-Code Replacement</SelectItem>
            <SelectItem value="ORIGINAL_CODE_IN_TRANSIT">In-Transit Replacement</SelectItem>
            <SelectItem value="NO_TAG">Untagged (No Tag)</SelectItem>
          </SelectContent>
        </Select>
        <Select value={source} onValueChange={resetToFirst(setSource)}>
          <SelectTrigger className="h-11 w-[150px] rounded-xl" data-testid="inv-review-source"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Sources</SelectItem>
            <SelectItem value="whatsapp_paste">WhatsApp Paste</SelectItem>
            <SelectItem value="screenshot_import">WhatsApp Screenshot</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={resetToFirst(setStatus)}>
          <SelectTrigger className="h-11 w-[150px] rounded-xl" data-testid="inv-review-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="unresolved">Unresolved</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <Input value={salesperson} onChange={(e) => resetToFirst(setSalesperson)(e.target.value)} placeholder="Salesperson" className="h-11 w-[150px] rounded-xl" data-testid="inv-review-salesperson" />
        <Input type="date" value={dateFrom} onChange={(e) => resetToFirst(setDateFrom)(e.target.value)} className="h-11 w-[150px] rounded-xl" data-testid="inv-review-date-from" />
        <Input type="date" value={dateTo} onChange={(e) => resetToFirst(setDateTo)(e.target.value)} className="h-11 w-[150px] rounded-xl" data-testid="inv-review-date-to" />
      </div>

      {/* Selection bar */}
      {selected.size > 0 && (
        <div className="mx-4 mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-primary/40 bg-primary/5 px-3 py-2 lg:mx-6" data-testid="inv-review-selection-bar">
          <span className="text-sm font-medium" data-testid="inv-review-selection-count">{selected.size} selected</span>
          <span className="text-xs text-muted-foreground">Marking reviewed keeps the item and does not change any invoice.</span>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="secondary" className="h-8 rounded-lg" data-testid="inv-review-bulk-review" onClick={() => setBulkOpen(true)}><ShieldCheck className="mr-1 h-3.5 w-3.5" /> Mark Reviewed</Button>
            <Button size="sm" variant="ghost" className="h-8 rounded-lg" data-testid="inv-review-selection-clear" onClick={clearSel}><X className="mr-1 h-3.5 w-3.5" /> Clear</Button>
          </div>
        </div>
      )}

      {/* Desktop table */}
      <div className="hidden px-6 py-4 lg:block">
        {isLoading ? <TableLoading /> : rows.length === 0 ? <EmptyState /> : (
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            <Table data-testid="inv-review-table">
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="w-10"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="inv-review-select-all" aria-label="Select all" disabled={selectableIds.length === 0} /></TableHead>
                  <TableHead>Sale Date</TableHead>
                  <TableHead>Original Code</TableHead>
                  <TableHead>Actual Code</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Store</TableHead>
                  <TableHead>Salesperson</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Age</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const rb = reasonBadge(r.reason);
                  return (
                    <TableRow key={r.garment_id} data-testid={`inv-review-row-${r.garment_id}`} data-state={selected.has(r.garment_id) ? 'selected' : undefined}>
                      <TableCell>{r.needs_review ? <Checkbox checked={selected.has(r.garment_id)} onCheckedChange={() => toggle(r.garment_id)} data-testid={`inv-review-select-${r.garment_id}`} /> : null}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm">{r.sale_date || '—'}</TableCell>
                      <TableCell className="font-mono text-xs">{r.original_code || '—'}</TableCell>
                      <TableCell className="font-mono text-xs font-medium">{r.actual_code}</TableCell>
                      <TableCell><Badge variant="outline" className={`${rb.cls} whitespace-nowrap text-[11px]`}>{rb.label}</Badge></TableCell>
                      <TableCell>{r.invoice_no
                        ? <button className="font-mono text-xs text-primary hover:underline" data-testid={`inv-review-invoice-${r.garment_id}`} onClick={() => navigate(`/invoice/${r.invoice_no}`)}>{r.invoice_no}</button>
                        : <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell><span className="inline-block rounded-md bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">{r.store_name}</span></TableCell>
                      <TableCell className="text-sm">{r.salesperson || '—'}</TableCell>
                      <TableCell className="text-right font-mono text-sm tabular-nums">{r.sale_amount != null ? money(r.sale_amount) : '—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{sourceLabel(r.source)}</TableCell>
                      <TableCell><Badge variant="outline" className={`${AGE_BADGE[r.age_bucket]} text-[11px]`}>{r.age_days}d</Badge></TableCell>
                      <TableCell>{r.needs_review
                        ? <Badge variant="outline" className="bg-amber-500/15 text-amber-600 border-amber-500/30 text-[11px]">Unresolved</Badge>
                        : <Badge variant="outline" className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 text-[11px]">{r.resolution_action === 'LINK' ? 'Linked' : r.resolution_action === 'RECODE' ? 'Recoded' : 'Reviewed'}</Badge>}</TableCell>
                      <TableCell>{r.needs_review
                        ? <Button size="sm" variant="outline" className="h-8 rounded-lg" data-testid={`inv-review-resolve-${r.garment_id}`} onClick={() => setResolveRow(r)}>Resolve</Button>
                        : <ChevronRight className="h-4 w-4 text-muted-foreground" />}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Mobile list */}
      <div className="px-4 py-3 lg:hidden">
        {isLoading ? <TableLoading /> : rows.length === 0 ? <EmptyState /> : (
          <div className="space-y-2" data-testid="inv-review-list">
            {rows.map((r) => {
              const rb = reasonBadge(r.reason);
              return (
                <div key={r.garment_id} className="rounded-xl border border-border bg-card p-3" data-testid={`inv-review-mrow-${r.garment_id}`}>
                  <div className="flex items-center gap-2">
                    {r.needs_review && <Checkbox checked={selected.has(r.garment_id)} onCheckedChange={() => toggle(r.garment_id)} data-testid={`inv-review-mselect-${r.garment_id}`} />}
                    <span className="font-mono text-sm font-semibold">{r.actual_code}</span>
                    <Badge variant="outline" className={`${rb.cls} ml-auto text-[10px]`}>{rb.label}</Badge>
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span>orig <span className="font-mono">{r.original_code || '—'}</span></span>
                    <span>{r.sale_date || '—'}</span>
                    <span>{r.store_name}</span>
                    {r.salesperson && <span>{r.salesperson}</span>}
                    <Badge variant="outline" className={`${AGE_BADGE[r.age_bucket]} text-[10px]`}>{r.age_days}d</Badge>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="text-sm">
                      {r.invoice_no && <button className="font-mono text-primary hover:underline" onClick={() => navigate(`/invoice/${r.invoice_no}`)}>{r.invoice_no}</button>}
                      <span className="ml-2 font-mono font-semibold">{r.sale_amount != null ? money(r.sale_amount) : ''}</span>
                    </div>
                    {r.needs_review
                      ? <Button size="sm" variant="outline" className="h-8 rounded-lg" data-testid={`inv-review-mresolve-${r.garment_id}`} onClick={() => setResolveRow(r)}>Resolve</Button>
                      : <Badge variant="outline" className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 text-[10px]">{r.resolution_action === 'LINK' ? 'Linked' : r.resolution_action === 'RECODE' ? 'Recoded' : 'Reviewed'}</Badge>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {rows.length > 0 && (
        <div className="flex items-center justify-between px-4 pb-28 lg:px-6" data-testid="inv-review-pagination">
          <span className="text-xs text-muted-foreground">{total} item{total === 1 ? '' : 's'} · page {page} of {pages}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="h-8 rounded-lg" disabled={page <= 1} data-testid="inv-review-prev" onClick={() => setPage((p) => Math.max(1, p - 1))}><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" className="h-8 rounded-lg" disabled={page >= pages} data-testid="inv-review-next" onClick={() => setPage((p) => Math.min(pages, p + 1))}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      <ResolveDialog row={resolveRow} onClose={() => setResolveRow(null)} onDone={() => { setResolveRow(null); clearSel(); refresh(); }} />
      <BulkReviewDialog open={bulkOpen} count={selected.size} onClose={() => setBulkOpen(false)}
        ids={[...selected]} onDone={() => { setBulkOpen(false); clearSel(); refresh(); }} />
    </Page>
  );
}

const TONE = { primary: 'text-primary', sky: 'text-sky-600', amber: 'text-amber-600', emerald: 'text-emerald-600', rose: 'text-rose-600' };
const SummaryCard = ({ icon: Icon, label, value, tone, active, onClick, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`rounded-2xl border bg-card p-4 text-left transition-colors ${active ? 'border-primary ring-1 ring-primary' : 'border-border hover:border-primary/60'}`}>
    <div className={`flex items-center gap-2 ${TONE[tone]}`}>
      <Icon className="h-4 w-4" />
      <span className="text-xs font-medium leading-tight">{label}</span>
    </div>
    <div className="mt-2 font-mono text-xl font-semibold tabular-nums lg:text-2xl">{value}</div>
  </button>
);

const TableLoading = () => (
  <div className="flex items-center gap-2 p-8 text-muted-foreground" data-testid="inv-review-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
);
const EmptyState = () => (
  <div className="rounded-2xl border border-border bg-card p-10 text-center text-muted-foreground" data-testid="inv-review-empty">
    <PackageSearch className="mx-auto mb-2 h-8 w-8 opacity-50" />
    Nothing here. All inventory exceptions are reconciled.
  </div>
);

const ACTIONS = [
  { key: 'LINK', label: 'Link to existing garment', icon: Link2, desc: 'Point this sale line to the correct real garment. It is marked sold; the placeholder is retired. Invoice total is unchanged.' },
  { key: 'RECODE', label: 'Correct the code', icon: PencilLine, desc: 'Rename this placeholder to the right barcode (must not already exist).' },
  { key: 'NO_CHANGE', label: 'Mark reviewed (no change)', icon: ShieldCheck, desc: 'Keep the placeholder as-is and just mark it reviewed.' },
];

function ResolveDialog({ row, onClose, onDone }) {
  const [action, setAction] = useState('LINK');
  const [target, setTarget] = useState('');
  const [newCode, setNewCode] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (row) { setAction('LINK'); setTarget(''); setNewCode(''); setNote(''); }
  }, [row]);

  const submit = async () => {
    if (action === 'LINK' && !target.trim()) { toast.error('Enter the correct garment barcode to link.'); return; }
    if (action === 'RECODE' && !newCode.trim()) { toast.error('Enter the corrected inventory code.'); return; }
    setBusy(true);
    try {
      const body = { action, note: note.trim() };
      if (action === 'LINK') body.target_barcode = target.trim();
      if (action === 'RECODE') body.new_code = newCode.trim();
      await api.post(`/inventory-review/${row.garment_id}/resolve`, body);
      toast.success(action === 'LINK' ? 'Linked to garment' : action === 'RECODE' ? 'Code corrected' : 'Marked reviewed');
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="inv-review-resolve-dialog">
        <DialogHeader>
          <DialogTitle>Resolve {row?.actual_code}</DialogTitle>
          <DialogDescription>
            Imported as <span className="font-mono">{row?.original_code}</span> · {reasonBadge(row?.reason).label}
            {row?.invoice_no ? <> · invoice <span className="font-mono">{row.invoice_no}</span></> : null}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {ACTIONS.map((a) => {
            const Icon = a.icon;
            const on = action === a.key;
            return (
              <button key={a.key} type="button" data-testid={`inv-review-action-${a.key}`} onClick={() => setAction(a.key)}
                className={`flex w-full items-start gap-3 rounded-xl border p-3 text-left transition-colors ${on ? 'border-primary ring-1 ring-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}>
                <Icon className={`mt-0.5 h-4 w-4 ${on ? 'text-primary' : 'text-muted-foreground'}`} />
                <div>
                  <p className="text-sm font-medium">{a.label}</p>
                  <p className="text-xs text-muted-foreground">{a.desc}</p>
                </div>
              </button>
            );
          })}
          {action === 'LINK' && (
            <Input value={target} onChange={(e) => setTarget(e.target.value.toUpperCase())} placeholder="Correct garment barcode (must be available)"
              className="h-11 rounded-xl font-mono" data-testid="inv-review-link-target" />
          )}
          {action === 'RECODE' && (
            <Input value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())} placeholder="Corrected inventory code"
              className="h-11 rounded-xl font-mono" data-testid="inv-review-recode-input" />
          )}
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Resolution note (optional)" rows={2}
            className="rounded-xl" data-testid="inv-review-note" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="inv-review-resolve-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="inv-review-resolve-submit">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />}Resolve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkReviewDialog({ open, count, ids, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { if (open) setNote(''); }, [open]);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post('/inventory-review/bulk-resolve', { garment_ids: ids, note: note.trim() });
      const d = r.data.data;
      toast.success(`${d.reviewed} marked reviewed${d.skipped ? ` · ${d.skipped} skipped` : ''}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="inv-review-bulk-dialog">
        <DialogHeader>
          <DialogTitle>Mark {count} item{count === 1 ? '' : 's'} reviewed</DialogTitle>
          <DialogDescription>These placeholders are kept as-is and flagged reviewed. No invoice is changed. Linking is never done in bulk.</DialogDescription>
        </DialogHeader>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" rows={2} className="rounded-xl" data-testid="inv-review-bulk-note" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="inv-review-bulk-submit">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-1.5 h-4 w-4" />}Mark Reviewed
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
